import React, { useCallback } from 'react';
import { format } from 'date-fns';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNAText, GridList, Iffy, luxColors } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACommonConfirmation,
  DNACustomDeckContextMenu,
  DNACustomDeckThumbnail,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNADocumentVersionContextMenu,
  DNADocumentVersionUpdateButton,
  DNAFolderStatusChip,
  useCurrentPage,
  useDNACustomDeckActions,
  useDNADocumentVersionActions,
} from 'src/components/DNA';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { useDispatch } from 'src/state/redux';
import { useAppSettings } from 'src/state/context/AppSettings';
import {
  CustomDeckORM,
  DocumentVersionORM,
  FolderItemORM,
  isCustomDeckORM,
  isDocumentVersionORM,
} from 'src/types/types';
import {
  COLUMN_CONFIG,
  FolderHeaderType,
  FolderVariants,
  GRID_LIST_STYLES_VARIANTS_CONFIGS,
} from 'src/screens/MyFolders/constants';
import { FileType, PermissionType } from '@alucio/aws-beacon-amplify/src/models';
import { getRootFolderORM } from 'src/state/redux/selector/folder';
import { isSharedEditableFolder } from 'src/utils/foldersHelpers';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import loggers from 'src/utils/logger';

const styles = StyleSheet.create({
  afterTextBadge: {
    marginLeft: 8,
  },
  badges: {
    justifyContent: 'flex-end',
  },
});

interface DNAFolderItemRowProps {
  folderItemORM: FolderItemORM;
  variant: FolderVariants;
  onPress: (folderItemORM: FolderItemORM) => void;
}

const DNAFolderItemRow: React.FC<DNAFolderItemRowProps> = (props) => {
  const { folderItemORM, variant, onPress } = props;
  const dispatch = useDispatch();
  const { deviceMode, isOnline } = useAppSettings();
  const documentVerActions = useDNADocumentVersionActions();
  const DNACustomDeckActions = useDNACustomDeckActions();
  const enableCustomDecksCollaborationEdit = useFeatureFlags('enableCustomDecksCollaborationEdit');
  const route = useCurrentPage({ exact: false });
  const isPublisherRoute = route?.PATH.includes('publisher');
  const isSharedFolders = route?.PATH.includes('shared_folders');
  const isTablet = deviceMode === 'tablet';
  const columns = COLUMN_CONFIG[variant].columns;
  let itemORM: DocumentVersionORM | CustomDeckORM;
  let unavailableContent = false;
  if (isDocumentVersionORM(folderItemORM.relations.itemORM)) {
    itemORM = folderItemORM.relations.itemORM;
    const { assets:{ isContentCached } } = itemORM.meta;
    unavailableContent = !isOnline && !isContentCached;
  } else if (isCustomDeckORM(folderItemORM.relations.itemORM)) {
    itemORM = folderItemORM.relations.itemORM;
    const { containsWebDocs, assets:{ isContentCached } } = itemORM.meta;
    unavailableContent = !isOnline && (!isContentCached || containsWebDocs);
  } else {
    console.warn('Unsupported itemORM type in DNAFolderItemRow', folderItemORM.relations.itemORM.type)
  }

  const handleRowPress = useCallback((isThumbnail?: boolean) => {
    // [TODO-2126] - We should have a property that combines isOnline and isContentCached
    //               on the CustomDeckORM rather than checking both at the component level
    if (isCustomDeckORM(folderItemORM.relations.itemORM) && !unavailableContent) {
      const customDeckORM = folderItemORM.relations.itemORM;
      const isReviewRequired = customDeckORM.meta.version.requiresReview;
      const withinGracePeriod = customDeckORM.meta.version.withinGracePeriod;
      const rootFolder = folderItemORM.relations.parentORM
        ? getRootFolderORM(folderItemORM.relations.parentORM)
        : folderItemORM.relations.parentORM;
      const hasEditPermission = rootFolder?.meta.permission === PermissionType.EDIT;
      const isSharedEditFolder = (rootFolder && isSharedEditableFolder(rootFolder)) || false;
      const isReviewAllowed = hasEditPermission &&
        ((!isSharedFolders || isSharedEditFolder) || enableCustomDecksCollaborationEdit)
      const openDeckEditor = DNACustomDeckActions.edit(customDeckORM, folderItemORM, 'REVIEW')

      if (isReviewRequired && isReviewAllowed && !withinGracePeriod) openDeckEditor()
      else if (!isReviewRequired || withinGracePeriod) {
        isThumbnail ? DNACustomDeckActions.present(customDeckORM, folderItemORM)() : onPress?.(folderItemORM)
      } else {
        const confirmModal = () => (
          <DNACommonConfirmation
            cancelText="OK"
            title=""
            // eslint-disable-next-line max-len
            descriptionText="This document is currently pending review; only editors may access until the review has been completed."
          />
        )
        const payload = {
          isVisible: true,
          allowBackdropCancel: true,
          component: confirmModal,
        };
        dispatch(DNAModalActions.setModal(payload));
      }
    } else if (!unavailableContent) onPress?.(folderItemORM)
  }, [folderItemORM, unavailableContent, DNACustomDeckActions, onPress, route, enableCustomDecksCollaborationEdit])

  return (
    <GridList.Row
      interactive="hoverable"
      hoverColor={luxColors.mainContainer.primary}
      onPress={() => handleRowPress()}
      style={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_ROW[deviceMode]}
    >
      {columns.map(col => {
        switch (col) {
          case FolderHeaderType.FAVORITE_BUTTON:
            if (isDocumentVersionORM(itemORM) && itemORM.meta.version.isLatestPublished) {
              const isBookmarked = itemORM.relations.documentORM.meta.bookmark.isBookmarked;
              return (
                <GridList.Col key={col}>
                  <DNAButton
                    appearance="ghost"
                    status={isBookmarked ? 'primary' : 'subtle'}
                    size={isTablet ? 'xl' : 'md'}
                    iconLeft={isBookmarked ? 'star-fill' : 'star'}
                    onPress={documentVerActions.bookmark(itemORM)}
                  />
                </GridList.Col>
              )
            } else {
              return <GridList.Col key={col}/>;
            }
          case FolderHeaderType.FOLDER_ICON_OR_THUMBNAIL:
            if (isDocumentVersionORM(itemORM)) {
              return (
                <GridList.Col key={col}>
                  <DNADocumentThumbnail.Hover
                    documentVersionORM={itemORM}
                    width={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_THUMBNAIL[deviceMode].width}
                    height={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_THUMBNAIL[deviceMode].height}
                    showProcessing
                    unavailableContent={unavailableContent}
                  />
                </GridList.Col>
              )
            } else if (isCustomDeckORM(itemORM)) {
              return (
                <GridList.Col key={col}>
                  <DNACustomDeckThumbnail.Hover
                    customDeckORM={itemORM}
                    width={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_THUMBNAIL[deviceMode].width}
                    height={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_THUMBNAIL[deviceMode].height}
                    unavailableContent={unavailableContent}
                  />
                </GridList.Col>
              )
            } else {
              return <GridList.Col key={col}/>;
            }
          case FolderHeaderType.NAME:
            if (isDocumentVersionORM(itemORM)) {
              return (
                <GridList.Col key={col}>
                  <DNAText
                    testID="folder-item-row-name"
                    numberOfLines={isTablet ? undefined : 1}
                    style={GRID_LIST_STYLES_VARIANTS_CONFIGS.TITLE_TEXT[deviceMode]}
                  >
                    {folderItemORM.meta.title}
                  </DNAText>
                  <Iffy is={unavailableContent}>
                    <DNAChip.Beacon variant="CONTENT_UNAVAILABLE" style={styles.afterTextBadge} />
                  </Iffy>
                  <Iffy is={!unavailableContent}>
                    <DNACustomFieldChipList documentVersionORM={itemORM} style={styles.afterTextBadge} />
                    <Iffy is={isPublisherRoute}>
                      <DNADocumentStatusChip
                        documentStatus={itemORM.relations.documentORM.model.status}
                        style={styles.afterTextBadge}
                      />
                    </Iffy>
                  </Iffy>
                </GridList.Col>
              )
            } else if (isCustomDeckORM(itemORM)) {
              return (
                <GridList.Col key={col}>
                  <DNAText
                    testID="folder-item-row-name"
                    numberOfLines={isTablet ? undefined : 1}
                    style={GRID_LIST_STYLES_VARIANTS_CONFIGS.TITLE_TEXT[deviceMode]}
                  >
                    {folderItemORM.meta.title}
                  </DNAText>
                  <DNAChip.Beacon
                    variant={unavailableContent ? 'CONTENT_UNAVAILABLE' : 'MODIFIED'}
                    style={styles.afterTextBadge}
                  />
                </GridList.Col>
              )
            } else {
              return <GridList.Col key={col}/>;
            }
          case FolderHeaderType.BADGES:
            return (
              <GridList.Col key={col} style={styles.badges}>
                <DNABox>
                  {isDocumentVersionORM(itemORM)
                    ? <DNADocumentVersionUpdateButton orm={folderItemORM} />
                    : (
                      <DNAFolderStatusChip
                        orm={folderItemORM}
                        variant="List"
                        onRowPress={handleRowPress}
                      />
                    )
                  }
                </DNABox>
              </GridList.Col>
            )
          case FolderHeaderType.ACTION_MENU:
            if (isDocumentVersionORM(itemORM)) {
              const unavailableContent = !isOnline && (!itemORM.meta.assets.isContentCached ||
                itemORM.model.type === FileType.WEB);
              return (
                <GridList.Col key={col}>
                  <DNADocumentVersionContextMenu
                    folderItemORM={folderItemORM}
                    unavailableContent={unavailableContent}
                  >
                    <DNAButton
                      testID="folder-item-ellipsis"
                      appearance="ghost"
                      status="subtle"
                      size={isTablet ? 'xl' : 'md'}
                      iconLeft="dots-three-vertical-bold"
                    />
                  </DNADocumentVersionContextMenu>
                </GridList.Col>
              )
            } else if (isCustomDeckORM(itemORM)) {
              return (
                <GridList.Col key={col}>
                  <DNACustomDeckContextMenu
                    folderItemORM={folderItemORM}
                    unavailableContent={unavailableContent}
                  >
                    <DNAButton
                      testID="folder-item-ellipsis"
                      appearance="ghost"
                      status="subtle"
                      size={isTablet ? 'xl' : 'md'}
                      iconLeft="dots-three-vertical-bold"
                    />
                  </DNACustomDeckContextMenu>
                </GridList.Col>
              )
            } else {
              return <GridList.Col key={col}/>;
            }

          case FolderHeaderType.UPDATED: {
            const lastUpdatedDate = isDocumentVersionORM(itemORM)
              ? itemORM.model.publishedAt ?? itemORM.model.updatedAt
              : itemORM.model.updatedAt;

            return (
              <GridList.Col key={col}>
                <DNAText
                  testID="folder-item-row-updated"
                  style={GRID_LIST_STYLES_VARIANTS_CONFIGS.SECONDARY_TEXT[deviceMode]}
                >
                  {format(new Date(lastUpdatedDate), 'MM/dd/yyyy')}
                </DNAText>
              </GridList.Col>
            )
          }
          default:
            loggers.util.warn('Unsupported type', col);
            return null;
        }
      })}
    </GridList.Row>
  )
}

export default DNAFolderItemRow;
